import { GridOptions } from "ag-grid-community";
import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import React, { memo, useMemo } from "react";

import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { gridTheme } from "@src/components/MainApp/SharedProductPicker/gridTheme";
import { ProductHeaderCheckBox } from "@src/components/MainApp/SharedProductPicker/list/ProductHeaderCheckBox";
import { ProductListHeader } from "@src/components/MainApp/SharedProductPicker/list/ProductListHeader";
import { ProductListProductHeader } from "@src/components/MainApp/SharedProductPicker/list/ProductListProductHeader";
import { SelectAllProgressBar } from "@src/components/MainApp/SharedProductPicker/list/SelectAllProgressBar";
import { useProductPickerContext } from "@src/components/MainApp/SharedProductPicker/ProductPickerContext";
import { ProductRenderParams } from "@src/components/MainApp/SharedProductPicker/types";
import { useSelector } from "@src/store";
import { isProductPickerSelectingAllProducts } from "@src/store/slices/productPicker";
import {
  GetProductPickerInfo,
  ProductPickerProduct,
} from "@src/store/slices/productPicker/types";

import { ProductListCheckBox } from "./ProductListCheckBox";
import { ProductListImage } from "./ProductListImage";
import { ProductListStatus } from "./ProductListStatus";
import { ProductListText } from "./ProductListText";

interface ProductListProps {
  getProductPickerInfo?: GetProductPickerInfo;
  isView?: boolean;
  showToolTipText?: boolean;
}

export const ProductList = memo((props: ProductListProps) => {
  const { dataSource } = useProductPickerContext();
  const columnDefs = useColumnDefs(props);
  const gridOptions = useGridOptions();
  const loading = useSelector(isProductPickerSelectingAllProducts);
  return (
    <Container sx={{ height: "435px" }}>
      <AgGridReact<ProductPickerProduct>
        serverSideDatasource={dataSource}
        columnDefs={columnDefs}
        gridOptions={gridOptions}
        loading={loading}
        loadingOverlayComponent={SelectAllProgressBar}
      />
    </Container>
  );
});

const Container = styled(Box)({
  marginRight: "16px",
  marginLeft: "16px",
  marginTop: "9px",
  flex: 1,
  "& .ag-body-viewport ": {
    overscrollBehavior: "contain",
  },
  "& .ag-root-wrapper": {
    borderRadius: 0,
  },
});

function useGridOptions() {
  return useMemo(() => {
    const gridOptions: GridOptions<ProductPickerProduct> = {
      theme: gridTheme,
      rowModelType: "serverSide",
      suppressAggFuncInHeader: true,
      headerHeight: 40,
      rowHeight: 60,
      cacheBlockSize: 250,
      rowSelection: { mode: "singleRow", checkboxes: false },
      getRowId: (params) => params.data.id,
      animateRows: false,
      cellSelection: false,
      stopEditingWhenCellsLoseFocus: true,
      undoRedoCellEditing: true,
      undoRedoCellEditingLimit: 25,
      suppressCellFocus: true,
      suppressMovableColumns: true,
      suppressContextMenu: true,
      suppressAdvancedFilterEval: true,
      suppressMenuHide: false,
      suppressAutoSize: true,
      enableBrowserTooltips: true,
      suppressHorizontalScroll: true,
    };
    return gridOptions;
  }, []);
}

function useColumnDefs({
  getProductPickerInfo,
  isView,
  showToolTipText,
}: ProductListProps) {
  return useMemo((): ColDef<ProductPickerProduct>[] => {
    const cellRendererParams: ProductRenderParams = {
      getProductPickerInfo,
      isView,
    };
    const defaultDef: ColDef<ProductPickerProduct> = {
      cellRendererParams,
      resizable: false,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
      suppressAutoSize: true,
    };
    return [
      {
        ...defaultDef,
        width: 60,
        headerComponent: ProductHeaderCheckBox,
        cellRenderer: ProductListCheckBox,
      },
      {
        ...defaultDef,
        width: 60,
        headerComponent: ProductListHeader,
        cellRenderer: ProductListImage,
      },
      {
        ...defaultDef,
        cellRendererParams: {
          ...cellRendererParams,
          showToolTipText,
        },
        headerComponent: ProductListProductHeader,
        flex: 1,
        cellRenderer: ProductListText,
        headerComponentParams: {
          sx: { alignItems: "flex-start" },
        },
      },
      {
        ...defaultDef,
        headerName: "Status",
        headerComponent: ProductListHeader,
        width: 80,
        cellRenderer: ProductListStatus,
        headerComponentParams: {
          sx: { alignItems: "center" },
        },
      },
    ];
  }, [getProductPickerInfo, isView]);
}
